import { SyntheticEvent, useState } from 'react';
import { VerticalTabContainer } from 'components/tab/Tab.styles';
import { Col, Nav, NavItem, Row, TabContainer } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import {
  Navigate,
  Outlet,
  useNavigate,
  useOutletContext,
} from 'react-router-dom';
import { normalizeKey } from 'utils/localeUtils';
import { Breakpoint, breakpoints } from 'styles/mediaQuery';
import useIsMobileScreen from 'hooks/useIsMobileScreen';
import useSidePanel from 'components/sidePanel/useSidePanel';
import {
  automationsSidePanelId,
  AutomationsTab,
} from './Automations.constants';
import useAutomationsActiveTab, {
  defaultAutomationTab,
} from './useAutomationsActiveTab';
import AutomationsHeader from './AutomationsHeader';

export const AutomationTabDefaultRoute = () => {
  return <Navigate to={defaultAutomationTab} replace />;
};

type AutomationsTabsContextType = {
  setShowNavTabs: (showNavTabs: boolean) => void;
  showNavTabs: boolean;
};

const AutomationsTabs = () => {
  const [showNavTabs, setShowNavTabs] = useState(true);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const activeTab = useAutomationsActiveTab();
  const { closeSidePanel } = useSidePanel({
    sidePanelId: automationsSidePanelId,
  });
  const isMobile = useIsMobileScreen(breakpoints[Breakpoint.XS]);

  const tabsToRender = Object.values(AutomationsTab);

  const onSelect = (event: SyntheticEvent<TabContainer, Event>) => {
    const tabName = event as unknown as string;

    navigate(tabName);
  };

  const getTabName = (tabName: string): string => {
    return t(normalizeKey(`pageAutomations.tabNames.${tabName}`));
  };

  const automationsTabs = (
    <Nav stacked>
      {tabsToRender.map((tabName: string) => (
        <NavItem
          data-testid={`automations-tab-${tabName}`}
          eventKey={tabName}
          key={tabName}
          onClick={() => closeSidePanel()}
        >
          {getTabName(tabName)}
        </NavItem>
      ))}
    </Nav>
  );

  return (
    <VerticalTabContainer activeKey={activeTab} onSelect={onSelect}>
      <Row>
        {showNavTabs && !isMobile && <Col sm={2}>{automationsTabs}</Col>}
        <Col sm={showNavTabs && !isMobile ? 10 : 12}>
          <AutomationsHeader
            hamburgerMenuContent={isMobile && showNavTabs && automationsTabs}
          />
          <Outlet
            context={
              {
                setShowNavTabs,
                showNavTabs,
              } satisfies AutomationsTabsContextType
            }
          />
        </Col>
      </Row>
    </VerticalTabContainer>
  );
};

export function useAutomationsTabs() {
  const context = useOutletContext<AutomationsTabsContextType>();

  // Return mock context for tests when outlet context is not available
  if (!context) {
    return {
      setShowNavTabs: () => {},
      showNavTabs: true,
    };
  }

  return context;
}

export default AutomationsTabs;
